import * as i1 from '@angular/common';
import { CommonModule } from '@angular/common';
import * as i0 from '@angular/core';
import { Pipe, EventEmitter, forwardRef, Output, Input, ViewEncapsulation, Component, NgModule } from '@angular/core';
import * as i3 from '@angular/forms';
import { Validators, FormsModule, NG_VALUE_ACCESSOR, NG_VALIDATORS } from '@angular/forms';
import * as i2 from '@engineering11/ui-lib/e11-icon';
import { E11IconModule } from '@engineering11/ui-lib/e11-icon';
import * as i4 from '@engineering11/ui-lib/e11-input-label';
import { E11InputLabelModule } from '@engineering11/ui-lib/e11-input-label';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';

class E11SelectOptionsPipe {
    transform(value) {
        return toSelectOptions(value);
    }
    static { this.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "18.2.13", ngImport: i0, type: E11SelectOptionsPipe, deps: [], target: i0.ɵɵFactoryTarget.Pipe }); }
    static { this.ɵpipe = i0.ɵɵngDeclarePipe({ minVersion: "14.0.0", version: "18.2.13", ngImport: i0, type: E11SelectOptionsPipe, isStandalone: true, name: "toSelectOptions" }); }
}
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "18.2.13", ngImport: i0, type: E11SelectOptionsPipe, decorators: [{
            type: Pipe,
            args: [{
                    name: 'toSelectOptions',
                    standalone: true,
                }]
        }] });
/** Converts an array of values to ISelectOption with { name: value, value: value } */
function toSelectOptions(value) {
    return value?.map((v) => ({ name: v, value: v })) ?? [];
}

/**
 * A custom select component for selecting an option from a list of provided options.
 * This component is a form control that works with Angular forms.
 * It provides optional validation, label, tooltips, and size configurations.
 *
 * @example
 * <e11-select [options]="options" [(ngModel)]="selectedOption" label="Select an option"></e11-select>
 */
class E11SelectComponent {
    /**
     * The class name to apply to the select input element.
     * @type {string}
     * @default ''
     * @deprecated use {@link inputClass} to fit convention
     * @see {@link inputClass}
     */
    set selectClass(inputClass) {
        this.inputClass = inputClass ?? '';
    }
    constructor(destroyRef) {
        this.destroyRef = destroyRef;
        /**
         * The unique identifier for the select component.
         * @type {string | undefined}
         * @default ''
         */
        this.id = '';
        /**
         * The size of the select component.
         * @type {Size}
         * @default 'md'
         */
        this.size = 'md';
        /**
         * The message to display when no option is selected.
         * @type {string}
         * @default 'Select an option'
         */
        this.selectMessage = 'Select an option';
        /**
         * The class name to apply to the label element.
         * @type {string}
         * @default ''
         */
        this.labelClass = '';
        /**
         * The class name to apply to the select input element.
         * @type {string}
         * @default ''
         */
        this.inputClass = '';
        /**
         * The class name to apply to the container element.
         * @type {string}
         * @default ''
         */
        this.containerClass = '';
        /**
         * The options to display in the select component.
         * @type {ISelectOption[]}
         * @default []
         */
        this.options = [];
        /**
         * Whether the select component has an error.
         * @type {boolean}
         * @default false
         */
        this.hasError = false;
        /**
         * Whether the select component is required.
         * @type {boolean}
         * @default false
         */
        this.required = false;
        /**
         * Whether the select component is disabled.
         * @type {boolean}
         * @default false
         */
        this.disabled = false;
        /**
         * Whether the select component is loading.
         * @type {boolean}
         * @default false
         */
        this.loading = false;
        /**
         * The text to display next to the optional input labels.
         * @type {string | undefined}
         * @default '(Optional)'
         */
        this.labelTextOptional = '(Optional)';
        /**
         * Emits the selected value when the option is changed.
         * @deprecated This event emitter is deprecated. Please use {@link onValueChange} instead.
         * @type {EventEmitter<any>}
         */
        this.valueChange = new EventEmitter();
        /**
         * Emits the selected value when the option is changed.
         * @type {EventEmitter<any>}
         */
        this.onValueChange = new EventEmitter();
    }
    ngOnInit() {
        this.checkRequired();
        this.listenForFormControlStatus();
    }
    /**
     * Listens for changes in the form control status and updates the error state accordingly.
     * @returns {void}
     */
    listenForFormControlStatus() {
        if (!this.parentForm || !this.formControlName) {
            return;
        }
        this.parentForm.controls[this.formControlName].statusChanges.pipe(takeUntilDestroyed(this.destroyRef)).subscribe((status) => {
            if (status === 'DISABLED') {
                this.hasError = false;
            }
        });
    }
    /**
     * Handles the change event for the select component.
     * @param {Event} event - The event object.
     * @returns {void}
     */
    onOptionChange(event) {
        this.hasError = false;
        const value = (event?.target).value;
        this.value = value;
        if (this.onChange) {
            this.onTouched();
            this.onChange(value);
        }
        this.valueChange.emit(value);
        if (!this.onChange && this.required && (value == '' || value === null || value == '')) {
            this.hasError = true;
        }
    }
    /**
     * Handles the change event for the select component.
     * @implements {ControlValueAccessor.writeValue}
     * @param {any} value - The value to write.
     * @returns {void}
     */
    writeValue(value) {
        this.value = value;
    }
    /**
     * Registers a callback function to be called when the select component value changes.
     * @implements {ControlValueAccessor.registerOnChange}
     * @param {Function} fn - The callback function to register.
     * @returns {void}
     */
    registerOnChange(fn) {
        this.onChange = fn;
    }
    /**
     * Registers a callback function to be called when the select component is touched.
     * @implements {ControlValueAccessor.registerOnTouched}
     * @param {Function} fn - The callback function to register.
     * @returns {void}
     */
    registerOnTouched(fn) {
        this.onTouched = fn;
    }
    /**
     * Sets the disabled state of the select component.
     * @implements {ControlValueAccessor.setDisabledState}
     * @param {boolean} isDisabled - The disabled state to set.
     * @returns {void}
     */
    setDisabledState(isDisabled) {
        this.disabled = isDisabled;
    }
    /**
     * Validates the select component.
     * @implements {Validator.validate}
     * @param {AbstractControl} control - The control to validate.
     * @returns {ValidationErrors} The validation errors.
     * @returns {null} If the control is valid.
     */
    validate(control) {
        if (control.hasValidator(Validators.required) && (control.value == '' || control.value === null || control.value === 'null')) {
            this.hasError = this.hasError ? true : control.touched;
            return {
                required: true,
            };
        }
        this.hasError = false;
        return null;
    }
    /**
     * Checks if the select component is required.
     * @returns {void}
     */
    checkRequired() {
        if (this.parentForm && this.formControlName) {
            const formControl = this.parentForm.controls[this.formControlName];
            this.required = formControl.hasValidator(Validators.required);
        }
    }
    static { this.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "18.2.13", ngImport: i0, type: E11SelectComponent, deps: [{ token: i0.DestroyRef }], target: i0.ɵɵFactoryTarget.Component }); }
    static { this.ɵcmp = i0.ɵɵngDeclareComponent({ minVersion: "14.0.0", version: "18.2.13", type: E11SelectComponent, isStandalone: true, selector: "e11-select", inputs: { id: "id", name: "name", label: "label", size: "size", selectMessage: "selectMessage", labelClass: "labelClass", inputClass: "inputClass", containerClass: "containerClass", parentForm: "parentForm", formControlName: "formControlName", options: "options", value: "value", hasError: "hasError", required: "required", disabled: "disabled", loading: "loading", tooltipConfig: "tooltipConfig", labelTextOptional: "labelTextOptional", selectClass: "selectClass" }, outputs: { valueChange: "valueChange", onValueChange: "onValueChange" }, providers: [
            {
                provide: NG_VALUE_ACCESSOR,
                useExisting: forwardRef(() => E11SelectComponent),
                multi: true,
            },
            {
                provide: NG_VALIDATORS,
                useExisting: forwardRef(() => E11SelectComponent),
                multi: true,
            },
        ], ngImport: i0, template: "<div class=\"e11-select {{ containerClass }}\">\n  <e11-input-label\n    [for]=\"name\"\n    [label]=\"label\"\n    [required]=\"required\"\n    [disabled]=\"disabled\"\n    [hasError]=\"hasError\"\n    [labelClassOverrides]=\"labelClass\"\n    [tooltipConfig]=\"tooltipConfig\"\n    [labelTextOptional]=\"labelTextOptional\"\n  ></e11-input-label>\n\n  <div class=\"e11-relative e11-rounded-md e11-shadow-sm\">\n    <select\n      class=\"e11-appearance-none e11-block e11-rounded-sm e11-w-full e11-pr-10 {{ inputClass }} disabled:e11-opacity-50 {{ size }} hover:e11-shadow\"\n      [ngClass]=\"{\n        'e11-border-skin-error e11-text-skin-error e11-placeholder-skin-error focus:e11-ring-skin-error focus:e11-border-skin-error': hasError,\n        'e11-border-skin-app-borders focus:e11-border-skin-primary-accent': !hasError,\n        'e11-bg-skin-app-bg': disabled,\n        'e11-skeleton e11-cursor-wait': loading,\n        'disabled:e11-cursor-not-allowed hover:e11-cursor-pointer': !loading\n      }\"\n      name=\"{{ name }}\"\n      [id]=\"id\"\n      (change)=\"onOptionChange($event)\"\n      [disabled]=\"disabled || loading\"\n    >\n      <option disabled [value]=\"null\" *ngIf=\"selectMessage\" [selected]=\"!value\">{{ selectMessage }}</option>\n\n      <option [value]=\"option.value\" *ngFor=\"let option of options\" [selected]=\"option.value === value\">\n        {{ option.name }}\n      </option>\n    </select>\n    <div *ngIf=\"hasError\" class=\"e11-absolute e11-inset-y-0 e11-right-0 e11-pr-3 e11-flex e11-items-center e11-pointer-events-none\">\n      <e11-icon-error></e11-icon-error>\n    </div>\n  </div>\n</div>\n", styles: ["select.sm{min-height:2rem;max-height:2rem;padding-top:0;padding-bottom:0;font-size:14px}select.md{min-height:42px;max-height:42px}select.lg{min-height:3.25rem;max-height:3.25rem}\n"], dependencies: [{ kind: "ngmodule", type: CommonModule }, { kind: "directive", type: i1.NgClass, selector: "[ngClass]", inputs: ["class", "ngClass"] }, { kind: "directive", type: i1.NgForOf, selector: "[ngFor][ngForOf]", inputs: ["ngForOf", "ngForTrackBy", "ngForTemplate"] }, { kind: "directive", type: i1.NgIf, selector: "[ngIf]", inputs: ["ngIf", "ngIfThen", "ngIfElse"] }, { kind: "ngmodule", type: E11IconModule }, { kind: "component", type: i2.E11IconErrorComponent, selector: "e11-icon-error", inputs: ["classOverrides"] }, { kind: "ngmodule", type: FormsModule }, { kind: "directive", type: i3.NgSelectOption, selector: "option", inputs: ["ngValue", "value"] }, { kind: "directive", type: i3.ɵNgSelectMultipleOption, selector: "option", inputs: ["ngValue", "value"] }, { kind: "ngmodule", type: E11InputLabelModule }, { kind: "component", type: i4.E11InputLabelComponent, selector: "e11-input-label", inputs: ["label", "id", "for", "size", "labelClassOverrides", "required", "hasError", "disabled", "darkMode", "requiredMode", "labelTextOptional", "description", "tooltipConfig"] }], encapsulation: i0.ViewEncapsulation.None }); }
}
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "18.2.13", ngImport: i0, type: E11SelectComponent, decorators: [{
            type: Component,
            args: [{ selector: 'e11-select', standalone: true, imports: [CommonModule, E11IconModule, FormsModule, E11InputLabelModule], providers: [
                        {
                            provide: NG_VALUE_ACCESSOR,
                            useExisting: forwardRef(() => E11SelectComponent),
                            multi: true,
                        },
                        {
                            provide: NG_VALIDATORS,
                            useExisting: forwardRef(() => E11SelectComponent),
                            multi: true,
                        },
                    ], encapsulation: ViewEncapsulation.None, template: "<div class=\"e11-select {{ containerClass }}\">\n  <e11-input-label\n    [for]=\"name\"\n    [label]=\"label\"\n    [required]=\"required\"\n    [disabled]=\"disabled\"\n    [hasError]=\"hasError\"\n    [labelClassOverrides]=\"labelClass\"\n    [tooltipConfig]=\"tooltipConfig\"\n    [labelTextOptional]=\"labelTextOptional\"\n  ></e11-input-label>\n\n  <div class=\"e11-relative e11-rounded-md e11-shadow-sm\">\n    <select\n      class=\"e11-appearance-none e11-block e11-rounded-sm e11-w-full e11-pr-10 {{ inputClass }} disabled:e11-opacity-50 {{ size }} hover:e11-shadow\"\n      [ngClass]=\"{\n        'e11-border-skin-error e11-text-skin-error e11-placeholder-skin-error focus:e11-ring-skin-error focus:e11-border-skin-error': hasError,\n        'e11-border-skin-app-borders focus:e11-border-skin-primary-accent': !hasError,\n        'e11-bg-skin-app-bg': disabled,\n        'e11-skeleton e11-cursor-wait': loading,\n        'disabled:e11-cursor-not-allowed hover:e11-cursor-pointer': !loading\n      }\"\n      name=\"{{ name }}\"\n      [id]=\"id\"\n      (change)=\"onOptionChange($event)\"\n      [disabled]=\"disabled || loading\"\n    >\n      <option disabled [value]=\"null\" *ngIf=\"selectMessage\" [selected]=\"!value\">{{ selectMessage }}</option>\n\n      <option [value]=\"option.value\" *ngFor=\"let option of options\" [selected]=\"option.value === value\">\n        {{ option.name }}\n      </option>\n    </select>\n    <div *ngIf=\"hasError\" class=\"e11-absolute e11-inset-y-0 e11-right-0 e11-pr-3 e11-flex e11-items-center e11-pointer-events-none\">\n      <e11-icon-error></e11-icon-error>\n    </div>\n  </div>\n</div>\n", styles: ["select.sm{min-height:2rem;max-height:2rem;padding-top:0;padding-bottom:0;font-size:14px}select.md{min-height:42px;max-height:42px}select.lg{min-height:3.25rem;max-height:3.25rem}\n"] }]
        }], ctorParameters: () => [{ type: i0.DestroyRef }], propDecorators: { id: [{
                type: Input
            }], name: [{
                type: Input
            }], label: [{
                type: Input
            }], size: [{
                type: Input
            }], selectMessage: [{
                type: Input
            }], labelClass: [{
                type: Input
            }], inputClass: [{
                type: Input
            }], containerClass: [{
                type: Input
            }], parentForm: [{
                type: Input
            }], formControlName: [{
                type: Input
            }], options: [{
                type: Input
            }], value: [{
                type: Input
            }], hasError: [{
                type: Input
            }], required: [{
                type: Input
            }], disabled: [{
                type: Input
            }], loading: [{
                type: Input
            }], tooltipConfig: [{
                type: Input
            }], labelTextOptional: [{
                type: Input
            }], selectClass: [{
                type: Input
            }], valueChange: [{
                type: Output
            }], onValueChange: [{
                type: Output
            }] } });

class E11SelectModule {
    static { this.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "18.2.13", ngImport: i0, type: E11SelectModule, deps: [], target: i0.ɵɵFactoryTarget.NgModule }); }
    static { this.ɵmod = i0.ɵɵngDeclareNgModule({ minVersion: "14.0.0", version: "18.2.13", ngImport: i0, type: E11SelectModule, imports: [E11SelectComponent, E11SelectOptionsPipe, CommonModule, FormsModule, E11InputLabelModule, E11IconModule], exports: [E11SelectComponent, E11SelectOptionsPipe] }); }
    static { this.ɵinj = i0.ɵɵngDeclareInjector({ minVersion: "12.0.0", version: "18.2.13", ngImport: i0, type: E11SelectModule, imports: [E11SelectComponent, CommonModule, FormsModule, E11InputLabelModule, E11IconModule] }); }
}
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "18.2.13", ngImport: i0, type: E11SelectModule, decorators: [{
            type: NgModule,
            args: [{
                    imports: [E11SelectComponent, E11SelectOptionsPipe, CommonModule, FormsModule, E11InputLabelModule, E11IconModule],
                    exports: [E11SelectComponent, E11SelectOptionsPipe],
                    providers: [],
                }]
        }] });

/**
 * Generated bundle index. Do not edit.
 */

export { E11SelectComponent, E11SelectModule, E11SelectOptionsPipe, toSelectOptions };
